import React, { Suspense, useCallback, useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { useAppSelector } from 'redux-store';

import dayjs from 'lib/dayjs-setup';

import type { ProductComponentAbstractProps } from 'containers/payments/ProductsListAbstract';

import type { IntervalTypeV3 } from 'domains/products/products.types';
import type { AppState } from 'redux-store';

import { Border, BorderTypeEnum } from 'components/domains/common/Border';

import { DiscountLabel } from 'components/domains/payments/DiscountLabel';
import type { CurrencyEnum } from 'components/domains/payments/FormattedPrice';
import { FormattedPrice } from 'components/domains/payments/FormattedPrice';
import { H3 } from 'components/domains/typography/Typography';

import dateToString from 'utils/dateToString';

import styles from './styles.module.scss';
import next from 'next';

export interface ProductMiniProps extends ProductComponentAbstractProps {
  className?: string;
  isSingle?: boolean;
}

export const ProductBlackFriday: React.FC<ProductMiniProps> = ({
  className,
  product,
  isSelected,
  isSingle = false,
  onClick,
}) => {
  const router = useRouter();

  const isAwUserQuery = router.query.is_aw_user as string | undefined;

  const profileStore = useAppSelector(state => state.profile);
  const { profile } = profileStore;

  let isAwUser = false;
  if (isAwUserQuery !== undefined) {
    isAwUser = true;
  }
  else if (!!profile?.is_aw_user) {
    isAwUser = true;
  }

  const theme = router.query.theme as string || 'blackfriday';
  const coupon = router.query.coupon as string | undefined;


  const status = isSelected
    ? BorderTypeEnum.SELECTED
    : BorderTypeEnum.NOT_ACTIVE;

  const {
    interval,
    discount_price: discountPrice,
    discount,
    price,
    currency,
  } = product.prices[0];

  const {
    title,
    is_current: isCurrent,
    next_payment,
  } = product;

  const onClickHandler = useCallback(() => {
    if (isCurrent) {
      return;
    }

    onClick(product);
  }, [isCurrent, product, onClick]);

  return (
    <>
      <Border
        type={status}
        className={cn(styles.border, {[styles.isSingle]: isSingle})}
        classNameSelected={cn(styles.borderSelected)}
        onClick={onClickHandler}
      >
        <div
          className={cn(styles.productCardContainer, className, {
            [styles.currentPlan]: status === BorderTypeEnum.SELECTED,
          })}
        >
          <ProductCard
            title={title}
            currency={currency as CurrencyEnum}
            discount={discount}
            price={price}
            discountPrice={discountPrice}
            interval={interval}
            isCurrent={isCurrent}
            isSingle={isSingle}
            isSelected={!!status}
            isAwUser={isAwUser}
            nextPayment={dateToString(next_payment)}
          />
        </div>
      </Border>
    </>
  );
};

const ProductCard: React.FC<{
  title: string;
  price: number | string;
  discount: number | string | null;
  discountPrice: number | string | null;
  currency: CurrencyEnum;
  interval?: IntervalTypeV3;
  isCurrent?: boolean;
  nextPayment?: string;
  isSingle: boolean;
  isSelected: boolean;
  isAwUser: boolean;
}> = ({ title, price, discount, currency, interval, isCurrent, isSingle, discountPrice, nextPayment, isSelected, isAwUser }) => {
  let content = null;

  const router = useRouter();

  if (isCurrent) {
    let paidText = 'Your current plan';
    if (nextPayment) {
      paidText += `, next payment ${nextPayment}`;
    }
    content = (
      <H3 className={styles.monthPrice}>
        <div className={styles.paidInterval}>{interval === 'year' ? 'One year' : 'Lifetime'}</div>
        <div className={styles.paidText}>{paidText}</div>
      </H3>
    );
  }
  else if (interval === 'year') {
    let pricePerMonth = (+price / 12).toFixed(2);
    if (discountPrice) {
      pricePerMonth = (+discountPrice / 12).toFixed(2);
    }
    content = (
      <div className={cn(styles.productYearWrapper)}>
        <div className={styles.productLifetime}>
          <div className={styles.productLifetimeInfoWrapper}>
            <div className={styles.productLifetimeTitle}>One Year</div>
            {discount && <div className={styles.productLifetimeDiscount}>-{+(discount)}%</div>}
          </div>
          <div className={styles.productLifetimePrice}>
            {discountPrice &&
              <div className={styles.productLifetimeDiscountPrice}>
                <FormattedPrice price={price} currency={currency} />
              </div>
            }
            <div className={cn(styles.productLifetimeFullPrice, {[styles.productDiscountPrice]: !!discountPrice})}>
              <FormattedPrice price={discountPrice ?? price} currency={currency} />{'/year'}
            </div>
          </div>
        </div>
        <div className={styles.productYearInfo}>
          <div className={styles.productYearPerPrice}>
            <FormattedPrice price={pricePerMonth} currency={currency} />{` per month`}
          </div>
        </div>
      </div>
    );
  }
  else if (interval === 'lifetime') {
    content = (
      <div className={styles.productLifetimeWrapper}>
        <div className={styles.productLifetime}>
          <div className={styles.productLifetimeInfoWrapper}>
            <div className={styles.productLifetimeTitle}>Lifetime</div>
            {discount && <div className={styles.productLifetimeDiscount}>-{+(discount)}%</div>}
          </div>
          <div className={styles.productLifetimePrice}>
            {discountPrice &&
              <div className={styles.productLifetimeDiscountPrice}>
                <FormattedPrice price={price} currency={currency} />
              </div>
            }
          <div className={cn(styles.productLifetimeFullPrice, {[styles.productDiscountPrice]: !!discountPrice})}>
            <FormattedPrice price={discountPrice ?? price} currency={currency} />{'/once'}
          </div>
          </div>
        </div>
        <div className={styles.productYearInfo}>
          <div className={styles.productYearPerPrice}>
            One-time payment
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.productCardWrapper)}>
      {content}
    </div>
  );
};